import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DatePicker, Select as AntSelect } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  FileCheck,
  FileText,
  Handshake,
  Info,
  Mail,
  PenLine,
  RefreshCw,
  type LucideIcon,
} from "lucide-react";
import { get } from "@/services/axiosUitils";
import { notify } from "@/components/common/Notification";
import { getApiErrorMessage } from "@/utils/extractApiErrorMessage";
import PtsOnboardingStatusService, {
  type OnboardingStatusRecord,
  type OnboardingStatusType,
} from "@/services/APIs/PtsOnboardingStatusService";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";

const ONBOARDING_STATUS_STEPS: {
  value: OnboardingStatusType;
  label: string;
  Icon: LucideIcon;
  iconBg: string;
}[] = [
  {
    value: "onboarding_email_sent",
    label: "Onboarding Email Sent",
    Icon: Mail,
    iconBg: "bg-blue-500",
  },
  {
    value: "perposal",
    label: "Proposal Sent",
    Icon: FileText,
    iconBg: "bg-amber-400",
  },
  {
    value: "perposal_signed",
    label: "Proposal Signed",
    Icon: PenLine,
    iconBg: "bg-emerald-500",
  },
  {
    value: "change_request",
    label: "Change Request",
    Icon: RefreshCw,
    iconBg: "bg-violet-500",
  },
  {
    value: "change_request_acknowledged",
    label: "Change Request Acknowledged & Approved",
    Icon: FileCheck,
    iconBg: "bg-orange-500",
  },
  {
    value: "third_party_services_acknowledge",
    label: "Third Party Services Acknowledge",
    Icon: Handshake,
    iconBg: "bg-teal-500",
  },
];

type EmployeeOption = { value: string; label: string };

type FormState = {
  statusType: string;
  date: string;
  sendBy: string;
  notes: string;
};

const EMPTY_FORM: FormState = {
  statusType: "",
  date: "",
  sendBy: "",
  notes: "",
};

const FIELD_CLASS =
  "w-full [&_.ant-select-selector]:!rounded-md [&_.ant-select-selector]:!bg-white [&_.ant-picker]:!rounded-md [&_.ant-picker]:!bg-white [&_.ant-picker]:!w-full";

function formatDisplayDate(value: string | null | undefined): string {
  if (!value) {
    return "--";
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return value;
  }
  return parsed.format("DD MMM YYYY");
}

function formatStatusLabel(status: string | null | undefined): string {
  if (!status) {
    return "Pending";
  }
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
}

function getStatusBadgeClass(status: string | null | undefined): string {
  const normalized = (status ?? "pending").toLowerCase();
  if (
    normalized === "send" ||
    normalized === "sent" ||
    normalized === "completed"
  ) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  return "bg-sky-50 text-sky-700 border-sky-200";
}

function filterSelectOption(
  input: string,
  option?: { label?: string | number | null }
) {
  return String(option?.label ?? "")
    .toLowerCase()
    .includes(input.trim().toLowerCase());
}

type ProjectOnboardingStatusSectionProps = {
  projectId: string;
};

const ProjectOnboardingStatusSection = ({
  projectId,
}: ProjectOnboardingStatusSectionProps) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: statusRecords = [], isLoading } = useQuery({
    queryKey: ["pts-onboarding-status", projectId],
    queryFn: () => PtsOnboardingStatusService.getByProjectId(projectId),
    enabled: !!projectId,
  });

  const { data: employeeOptions = [], isLoading: employeesLoading } = useQuery({
    queryKey: ["full-employee-list", "onboarding-status"],
    queryFn: async () => {
      const response = await get<unknown>("/pts_app/full-employee-list/");
      const rows = Array.isArray((response as { data?: unknown })?.data)
        ? (response as { data: unknown[] }).data
        : Array.isArray(response)
          ? response
          : [];

      return rows
        .map((employee: Record<string, unknown>) => {
          const value = employee?.emp_id ?? employee?.id ?? employee?.value;
          const label = employee?.emp_name ?? employee?.name ?? employee?.label;
          if (!value) {
            return null;
          }
          return {
            value: String(value),
            label: label ? String(label) : String(value),
          };
        })
        .filter(Boolean) as EmployeeOption[];
    },
  });

  const employeeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    employeeOptions.forEach(option => {
      map.set(option.value, option.label);
    });
    return map;
  }, [employeeOptions]);

  const statusByType = useMemo(() => {
    const map = new Map<string, OnboardingStatusRecord>();
    statusRecords.forEach(record => {
      if (record?.status_type) {
        map.set(String(record.status_type), record);
      }
    });
    return map;
  }, [statusRecords]);

  const statusOptions = useMemo(
    () =>
      ONBOARDING_STATUS_STEPS.map(step => ({
        value: step.value,
        label: step.label,
      })),
    []
  );

  const lastSentStepIndex = useMemo(
    () =>
      ONBOARDING_STATUS_STEPS.reduce((lastIndex, step, index) => {
        const status = String(
          statusByType.get(step.value)?.status ?? "pending"
        ).toLowerCase();
        return ["send", "sent", "completed"].includes(status)
          ? index
          : lastIndex;
      }, -1),
    [statusByType]
  );

  const resolveSentByName = (record?: OnboardingStatusRecord): string => {
    if (!record?.send_by) {
      return "--";
    }
    if (record.send_by_name) {
      return String(record.send_by_name);
    }
    return employeeNameMap.get(String(record.send_by)) ?? "--";
  };

  const handleStatusTypeChange = (statusType: string | null | undefined) => {
    if (!statusType) {
      setForm(EMPTY_FORM);
      return;
    }
    const existing = statusByType.get(statusType);
    setForm({
      statusType,
      date: existing?.date ? String(existing.date).slice(0, 10) : "",
      sendBy: existing?.send_by ? String(existing.send_by) : "",
      notes: existing?.notes ? String(existing.notes) : "",
    });
  };

  const handleDateChange = (value: Dayjs | null) => {
    setForm(prev => ({
      ...prev,
      date: value ? value.format("YYYY-MM-DD") : "",
    }));
  };

  const handleUpdateStatus = async () => {
    if (!form.statusType) {
      notify.error("Please select a status");
      return;
    }
    if (!form.date) {
      notify.error("Please select a date");
      return;
    }
    if (!form.sendBy) {
      notify.error("Please select who sent this status");
      return;
    }

    setIsSubmitting(true);
    try {
      const existing = statusByType.get(form.statusType);
      const payload = {
        status_type: form.statusType,
        status: "send",
        date: form.date,
        send_by: form.sendBy,
        notes: form.notes.trim() || undefined,
      };

      if (existing?.id) {
        await PtsOnboardingStatusService.update(String(existing.id), payload);
        notify.success("Onboarding status updated");
      } else {
        await PtsOnboardingStatusService.create({
          project_id: projectId,
          ...payload,
        });
        notify.success("Onboarding status created");
      }

      await queryClient.invalidateQueries({
        queryKey: ["pts-onboarding-status", projectId],
      });
      setForm(EMPTY_FORM);
    } catch (error: unknown) {
      notify.error(
        getApiErrorMessage(
          error,
          "Failed to update onboarding status. Please try again."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="overflow-x-auto pb-2">
        <div className="relative flex min-w-[920px] items-start justify-between gap-2 px-2 pt-2">
          <div className="absolute left-[8%] right-[8%] top-10 z-0 h-0.5 bg-gray-200">
            <div
              className="bg-primary h-full transition-[width] duration-300"
              style={{
                width:
                  lastSentStepIndex > 0
                    ? `${(lastSentStepIndex / (ONBOARDING_STATUS_STEPS.length - 1)) * 100}%`
                    : "0%",
              }}
            />
          </div>

          {ONBOARDING_STATUS_STEPS.map(step => {
            const record = statusByType.get(step.value);
            const statusValue = record?.status ?? "pending";
            const isSelected = form.statusType === step.value;
            const Icon = step.Icon;

            return (
              <button
                key={step.value}
                type="button"
                onClick={() => handleStatusTypeChange(step.value)}
                className={cn(
                  "relative z-10 flex w-[140px] flex-col items-center gap-2 rounded-lg p-2 text-center transition-colors",
                  isSelected
                    ? "bg-slate-50 ring-1 ring-slate-200"
                    : "hover:bg-slate-50"
                )}
              >
                <div
                  className={cn(
                    "flex h-12 w-12 items-center justify-center rounded-full text-white shadow-sm",
                    step.iconBg
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold leading-tight text-[#111827]">
                    {step.label}
                  </p>
                  <p className="text-[11px] text-[#627384]">
                    Date: {formatDisplayDate(record?.date)}
                  </p>
                  <p className="text-[11px] text-[#627384]">
                    Sent by: {resolveSentByName(record)}
                  </p>
                  <span
                    className={cn(
                      "inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-medium",
                      getStatusBadgeClass(statusValue)
                    )}
                  >
                    {formatStatusLabel(statusValue)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-[#111827]">
              Quick Status Update
            </h3>
            <Info className="h-4 w-4 text-[#627384]" />
          </div>
          <Button
            type="button"
            onClick={handleUpdateStatus}
            disabled={isSubmitting || isLoading}
            className="bg-primary hover:bg-primary/90 w-full text-white sm:w-auto"
          >
            {isSubmitting ? "Updating..." : "Update Status"}
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label>
              Select Status <span className="text-red-500">*</span>
            </Label>
            <AntSelect
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select status"
              className={FIELD_CLASS}
              value={form.statusType || undefined}
              onChange={handleStatusTypeChange}
              options={statusOptions}
              filterOption={filterSelectOption}
              getPopupContainer={trigger =>
                trigger.parentElement ?? document.body
              }
            />
          </div>

          <div className="space-y-2">
            <Label>
              Date <span className="text-red-500">*</span>
            </Label>
            <DatePicker
              allowClear
              placeholder="dd-mm-yyyy"
              format="DD-MM-YYYY"
              className={cn(FIELD_CLASS, "h-8")}
              value={form.date ? dayjs(form.date) : null}
              onChange={handleDateChange}
              getPopupContainer={trigger =>
                trigger.parentElement ?? document.body
              }
            />
          </div>

          <div className="space-y-2">
            <Label>
              Sent By <span className="text-red-500">*</span>
            </Label>
            <AntSelect
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select Person"
              loading={employeesLoading}
              className={FIELD_CLASS}
              value={form.sendBy || undefined}
              onChange={value =>
                setForm(prev => ({ ...prev, sendBy: value ?? "" }))
              }
              options={employeeOptions}
              filterOption={filterSelectOption}
              getPopupContainer={trigger =>
                trigger.parentElement ?? document.body
              }
            />
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <Label>Notes (Optional)</Label>
          <Textarea
            value={form.notes}
            onChange={event =>
              setForm(prev => ({ ...prev, notes: event.target.value }))
            }
            placeholder="Enter notes..."
            className="min-h-[50px] bg-white"
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectOnboardingStatusSection;
